import React, { useState, useEffect } from 'react';
import { usersAPI } from '../services/api';
import { MobileCard } from '../components/MobileCard';
import PasswordInput from '../components/common/PasswordInput';
import '../components/MobileCard.css';
import './UserManagement.css';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  role_display: string;
  is_active: boolean;
  date_joined: string;
  last_login: string | null;
  is_mill_worker: boolean;
  has_admin_portal_access: boolean;
  has_crm_access: boolean;
  has_store_portal_access: boolean;
  has_delivery_portal_access: boolean;   // read-only derived mirror (for the badge)
  delivery_role: string;                 // 'none' | 'driver' | 'manager'
}

interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  password: string;
  role: string;
  is_active: boolean;
  has_admin_portal_access: boolean;
  is_mill_worker: boolean;
  has_store_portal_access: boolean;
  has_crm_access: boolean;
  delivery_role: string;
}

const ROLE_OPTIONS = [
  { value: 'ADMIN', label: 'Administrator' },
  { value: 'OFFICE', label: 'Office Staff' },
  { value: 'PRODUCTION', label: 'Production Staff' },
  { value: 'MILL_WORKER', label: 'Mill Worker' },
  { value: 'WAREHOUSE', label: 'Warehouse Staff' },
  { value: 'READONLY', label: 'Read-only User' },
];

// Delivery Portal role — its own axis. Manager sees everything; driver sees only
// their route (route view is a later build). 'none' = no delivery access.
const DELIVERY_ROLE_OPTIONS = [
  { value: 'none', label: 'No delivery access' },
  { value: 'driver', label: 'Driver - works routes on the phone' },
  { value: 'manager', label: 'Manager - builds and dispatches routes, and drives' },
];

const emptyFormData: UserFormData = {
  first_name: '',
  last_name: '',
  email: '',
  username: '',
  password: '',
  role: 'READONLY',
  is_active: true,
  has_admin_portal_access: false,
  is_mill_worker: false,
  has_store_portal_access: false,
  has_crm_access: false,
  delivery_role: 'none',
};

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>(emptyFormData);
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll();
      const userData = response.data.results || response.data;
      setUsers(userData);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    // Filter by search term
    const matchesSearch = !searchTerm ||
      (user.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase());

    // Filter by role
    const matchesRole = !selectedRole || user.role === selectedRole;

    // Filter by active status
    const matchesActive = showInactive || user.is_active;

    return matchesSearch && matchesRole && matchesActive;
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const openAddModal = () => {
    setFormData(emptyFormData);
    setShowAddModal(true);
    setError('');
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      username: user.username,
      password: '', // Don't populate password for edit
      role: user.role,
      is_active: user.is_active,
      has_admin_portal_access: user.has_admin_portal_access,
      is_mill_worker: user.is_mill_worker,
      has_store_portal_access: user.has_store_portal_access,
      has_crm_access: user.has_crm_access,
      delivery_role: user.delivery_role,
    });
    setShowEditModal(true);
    setError('');
  };

  const openResetPasswordModal = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetPasswordModal(true);
    setError('');
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.username || !formData.password) {
      setError('First name, last name, username, and password are required');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      setSaving(true);
      await usersAPI.create(formData);
      setShowAddModal(false);
      setSuccessMessage('User created successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadUsers();
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.error || err.response?.data?.username?.[0] || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    if (!formData.first_name || !formData.last_name || !formData.username) {
      setError('First name, last name, and username are required');
      return;
    }

    try {
      setSaving(true);
      const updateData = { ...formData };
      if (!updateData.password) {
        delete (updateData as any).password; // Don't send empty password
      }
      await usersAPI.patch(selectedUser.id, updateData);
      setShowEditModal(false);
      setSuccessMessage('User updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadUsers();
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      setSaving(true);
      await usersAPI.resetPassword(selectedUser.id, newPassword);
      setShowResetPasswordModal(false);
      setSuccessMessage(`Password reset for ${selectedUser.username}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Error resetting password:', err);
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await usersAPI.toggleActive(user.id);
      setSuccessMessage(`User ${user.username} is now ${user.is_active ? 'inactive' : 'active'}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      loadUsers();
    } catch (err: any) {
      console.error('Error toggling user status:', err);
      setError(err.response?.data?.error || 'Failed to toggle user status');
    }
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
    setError('');
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setDeleting(true);
      await usersAPI.delete(selectedUser.id);
      setShowDeleteModal(false);
      setSuccessMessage(`User ${selectedUser.username} has been permanently deleted`);
      setTimeout(() => setSuccessMessage(''), 3000);
      loadUsers();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h1>User Management</h1>
        <button className="btn btn-primary" onClick={openAddModal}>
          + Add Employee
        </button>
      </div>

      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}

      {error && !showAddModal && !showEditModal && !showResetPasswordModal && (
        <div className="alert alert-error">{error}</div>
      )}

      <div className="user-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, username, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          className="role-filter"
        >
          <option value="">All Roles</option>
          {ROLE_OPTIONS.map(role => (
            <option key={role.value} value={role.value}>{role.label}</option>
          ))}
        </select>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show Inactive
        </label>
      </div>

      <div className="desktop-table-container">
        <table className="user-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Portal Access</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={8} className="no-results">No users found</td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id} className={!user.is_active ? 'inactive-row' : ''}>
                  <td>
                    <strong>{user.first_name} {user.last_name}</strong>
                  </td>
                  <td>{user.username}</td>
                  <td>{user.email || '-'}</td>
                  <td>
                    <span className={`role-badge role-${user.role.toLowerCase()}`}>
                      {user.role_display || user.role}
                    </span>
                  </td>
                  <td>
                    <div className="portal-access-badges">
                      {user.has_admin_portal_access && (
                        <span className="access-badge admin">Admin</span>
                      )}
                      {user.is_mill_worker && (
                        <span className="access-badge mill">Mill</span>
                      )}
                      {user.has_store_portal_access && (
                        <span className="access-badge store">Store</span>
                      )}
                      {user.has_crm_access && (
                        <span className="access-badge crm">CRM</span>
                      )}
                      {user.has_delivery_portal_access && (
                        <span className="access-badge delivery">Delivery</span>
                      )}
                      {!user.has_admin_portal_access && !user.is_mill_worker &&
                       !user.has_store_portal_access && !user.has_crm_access &&
                       !user.has_delivery_portal_access && (
                        <span className="access-badge none">None</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{formatDateTime(user.last_login)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openEditModal(user)}
                        title="Edit"
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => openResetPasswordModal(user)}
                        title="Reset Password"
                      >
                        Reset Pwd
                      </button>
                      <button
                        className={`btn btn-sm ${user.is_active ? 'btn-outline-danger' : 'btn-success'}`}
                        onClick={() => handleToggleActive(user)}
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => openDeleteModal(user)}
                        title="Delete permanently"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards (shown on small screens) */}
      <div className="mobile-cards-container">
        <div className="mobile-card-list">
          {filteredUsers.length === 0 ? (
            <div className="no-data-card">No users found</div>
          ) : (
            filteredUsers.map(user => (
              <MobileCard
                key={user.id}
                title={`${user.first_name} ${user.last_name}`}
                subtitle={user.username}
                badge={
                  <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                }
                fields={[
                  { label: 'Role', value: <span className={`role-badge role-${user.role.toLowerCase()}`}>{user.role_display || user.role}</span> },
                  { label: 'Email', value: user.email || '-' },
                  { label: 'Last Login', value: formatDateTime(user.last_login) },
                ]}
                actions={
                  <>
                    <button className="btn btn-sm btn-secondary" onClick={() => openEditModal(user)}>
                      Edit
                    </button>
                    <button className="btn btn-sm btn-warning" onClick={() => openResetPasswordModal(user)}>
                      Reset Pwd
                    </button>
                  </>
                }
                className={!user.is_active ? 'status-inactive' : ''}
              />
            ))
          )}
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Employee</h2>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleAddUser}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}

                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Username *</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Temporary Password *</label>
                    <PasswordInput
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Minimum 8 characters"
                      required
                      minLength={8}
                    />
                  </div>
                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                    >
                      {ROLE_OPTIONS.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Portal Access</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="has_admin_portal_access"
                        checked={formData.has_admin_portal_access}
                        onChange={handleInputChange}
                      />
                      Admin Portal
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_mill_worker"
                        checked={formData.is_mill_worker}
                        onChange={handleInputChange}
                      />
                      Mill Portal
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="has_store_portal_access"
                        checked={formData.has_store_portal_access}
                        onChange={handleInputChange}
                      />
                      Store Portal
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="has_crm_access"
                        checked={formData.has_crm_access}
                        onChange={handleInputChange}
                      />
                      CRM
                    </label>
                    <label className="select-label" style={{ display: 'block' }}>
                      Delivery Portal
                      <select
                        name="delivery_role"
                        value={formData.delivery_role}
                        onChange={handleInputChange}
                        style={{ display: 'block', marginTop: 4 }}
                      >
                        {DELIVERY_ROLE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    Active
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Employee: {selectedUser.first_name} {selectedUser.last_name}</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleEditUser}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}

                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Username *</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                    >
                      {ROLE_OPTIONS.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Member Since</label>
                    <input
                      type="text"
                      value={formatDate(selectedUser.date_joined)}
                      disabled
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Portal Access</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="has_admin_portal_access"
                        checked={formData.has_admin_portal_access}
                        onChange={handleInputChange}
                      />
                      Admin Portal
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_mill_worker"
                        checked={formData.is_mill_worker}
                        onChange={handleInputChange}
                      />
                      Mill Portal
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="has_store_portal_access"
                        checked={formData.has_store_portal_access}
                        onChange={handleInputChange}
                      />
                      Store Portal
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="has_crm_access"
                        checked={formData.has_crm_access}
                        onChange={handleInputChange}
                      />
                      CRM
                    </label>
                    <label className="select-label" style={{ display: 'block' }}>
                      Delivery Portal
                      <select
                        name="delivery_role"
                        value={formData.delivery_role}
                        onChange={handleInputChange}
                        style={{ display: 'block', marginTop: 4 }}
                      >
                        {DELIVERY_ROLE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    Active
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowResetPasswordModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button className="close-btn" onClick={() => setShowResetPasswordModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}

                <p>Reset password for <strong>{selectedUser.first_name} {selectedUser.last_name}</strong> ({selectedUser.username})</p>

                <div className="form-group">
                  <label>New Password *</label>
                  <PasswordInput
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowResetPasswordModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-warning" disabled={saving}>
                  {saving ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header modal-header-danger">
              <h2>Delete User</h2>
              <button className="close-btn" onClick={() => setShowDeleteModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}

              <div className="delete-warning">
                <p>Are you sure you want to permanently delete this user?</p>
                <div className="delete-user-info">
                  <strong>{selectedUser.first_name} {selectedUser.last_name}</strong>
                  <span>({selectedUser.username})</span>
                </div>
                <p className="delete-warning-text">
                  This action cannot be undone. All data associated with this user will be permanently removed.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteUser}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
